/**
 * Class F (fixed Gregorian) and Class A (closed-form algorithm).
 *
 * Everything here is correct forever and needs no table, no horizon and no
 * human review. That is worth stating because it is most of the calendar: of
 * the ~55 occasions this app ships, only the Hindu lunisolar family, Nowruz and
 * Loy Krathong genuinely require a typed-in table.
 */

import type { CivilDate } from '../../shared/config.ts';

import { nowruz } from './astro.ts';

/** Anonymous Gregorian computus. Exact for the Gregorian calendar. */
export function easterWestern(y: number): CivilDate {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { y, m: month, d: day };
}

/**
 * Meeus/Julian computus, converted to Gregorian.
 *
 * The 13-day Julian→Gregorian offset holds for 1900–2099. Past 2100 it becomes
 * 14 and this function is wrong — which is far beyond HORIZON_YEAR, but the
 * constant is named rather than inlined so the reason is findable.
 */
const JULIAN_GREGORIAN_OFFSET = 13;

export function easterOrthodox(y: number): CivilDate {
  const a = y % 4;
  const b = y % 7;
  const c = y % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31);
  const day = ((d + e + 114) % 31) + 1;
  const t = new Date(Date.UTC(y, month - 1, day + JULIAN_GREGORIAN_OFFSET));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

/** The nth `weekday` of a month; `n = -1` means the last one. 0 = Sunday. */
export function nthWeekday(y: number, m: number, weekday: number, n: number): CivilDate {
  if (n > 0) {
    const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
    const day = 1 + ((weekday - first + 7) % 7) + (n - 1) * 7;
    return { y, m, d: day };
  }
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const last = new Date(Date.UTC(y, m - 1, lastDay)).getUTCDay();
  return { y, m, d: lastDay - ((last - weekday + 7) % 7) };
}

export function addDays(c: CivilDate, n: number): CivilDate {
  const t = new Date(Date.UTC(c.y, c.m - 1, c.d + n));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

/**
 * Oktoberfest opens on the Saturday after September 15 and runs to the first
 * Sunday of October — extended to October 3 when that Sunday is October 1 or 2,
 * so the festival always covers German Unity Day. Only the opening date is
 * returned here; the length is expressed as the occasion's default padding.
 */
export function oktoberfestStart(y: number): CivilDate {
  const sep15 = new Date(Date.UTC(y, 8, 15)).getUTCDay();
  // Days from Sep 15 to the next Saturday. If Sep 15 IS a Saturday, the
  // festival opens a week later — the rule is "the Saturday AFTER".
  const delta = (6 - sep15 + 7) % 7 || 7;
  return { y, m: 9, d: 15 + delta };
}

export function oktoberfestEnd(y: number): CivilDate {
  const firstSunOct = nthWeekday(y, 10, 0, 1);
  return firstSunOct.d <= 2 ? { y, m: 10, d: 3 } : firstSunOct;
}

/** The four Sundays of Advent; the first is the 4th Sunday before Christmas. */
export function adventStart(y: number): CivilDate {
  const christmas = { y, m: 12, d: 25 };
  const dow = new Date(Date.UTC(y, 11, 25)).getUTCDay();
  // Back up to the Sunday on or before Dec 24, then three more weeks.
  const toSunday = dow === 0 ? 7 : dow;
  return addDays(christmas, -toSunday - 21);
}

/** Fixed Gregorian dates. `[month, day]`. */
export const FIXED: Record<string, [number, number]> = {
  newyear: [1, 1],
  pongal: [1, 14],
  'republic-day-in': [1, 26],
  valentines: [2, 14],
  'womens-day': [3, 8],
  stpatricks: [3, 17],
  'april-fools': [4, 1],
  songkran: [4, 13],
  'earth-day': [4, 22],
  'may-day': [5, 1],
  'canada-day': [7, 1],
  'independence-day-us': [7, 4],
  'bastille-day': [7, 14],
  'independence-day-in': [8, 15],
  halloween: [10, 31],
  'day-of-the-dead': [11, 1],
  'singles-day': [11, 11],
  'free-shipping-day': [12, 14],
  christmas: [12, 25],
  boxingday: [12, 26],
  newyearseve: [12, 31],
};

/** Closed-form rules. Returns null for an id this module does not own. */
export function algorithmicDate(id: string, y: number): CivilDate | null {
  const fixed = FIXED[id];
  if (fixed) return { y, m: fixed[0], d: fixed[1] };

  switch (id) {
    case 'easter':
      return easterWestern(y);
    case 'easter-orthodox':
      return easterOrthodox(y);
    case 'good-friday':
      return addDays(easterWestern(y), -2);
    case 'ash-wednesday':
      return addDays(easterWestern(y), -46);
    case 'mardi-gras':
      return addDays(easterWestern(y), -47);
    case 'mothering-sunday':
      // UK/IE Mother's Day: the fourth Sunday of Lent, i.e. Easter − 21.
      return addDays(easterWestern(y), -21);
    case 'pentecost':
      return addDays(easterWestern(y), 49);

    // nth-weekday rules
    case 'mlk-day':
      return nthWeekday(y, 1, 1, 3);
    case 'presidents-day':
      return nthWeekday(y, 2, 1, 3);
    case 'memorial-day':
      return nthWeekday(y, 5, 1, -1);
    case 'mothersday':
      return nthWeekday(y, 5, 0, 2);
    case 'mothersday-uk':
      return addDays(easterWestern(y), -21);
    case 'fathersday':
      return nthWeekday(y, 6, 0, 3);
    case 'fathersday-au':
      return nthWeekday(y, 9, 0, 1);
    case 'fathersday-br':
      return nthWeekday(y, 8, 0, 2);
    case 'labor-day-us':
      return nthWeekday(y, 9, 1, 1);
    case 'thanksgiving-ca':
      return nthWeekday(y, 10, 1, 2);
    case 'thanksgiving-us':
      return nthWeekday(y, 11, 4, 4);
    // The three highest-value dates in the product, all derived from one rule.
    case 'blackfriday':
      return addDays(nthWeekday(y, 11, 4, 4), 1);
    case 'cybermonday':
      return addDays(nthWeekday(y, 11, 4, 4), 4);
    case 'bfcm':
      // The window template: Thanksgiving − 1 through Cyber Monday. The occasion
      // DATE is Thanksgiving itself; the span is the occasion's default padding.
      return nthWeekday(y, 11, 4, 4);

    case 'nowruz': {
      // The March equinox at Tehran. Computed rather than tabled — see astro.ts.
      // The old hand-typed table had 2026 and 2030 wrong by a day.
      const n = nowruz(y);
      return { y: n.y, m: n.m, d: n.d };
    }

    case 'oktoberfest':
      return oktoberfestStart(y);
    case 'advent':
      return adventStart(y);

    default:
      return null;
  }
}
